import { RadioChangeEvent } from "antd/lib/radio";
import { Moment } from "moment";

import { AntdIconProps } from "@ant-design/icons/lib/components/AntdIcon";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { UploadFile } from "antd/lib/upload/interface";
import { Rule, RuleType } from "rc-field-form/lib/interface";

export interface Department {
  id: string;
  name: string;
  key?: string | number;
  align?: string;
  users: User[] | [];
}

export interface SubMenuItemInterface {
  route: string;
  name: string;
  icon: React.ForwardRefExoticComponent<
    AntdIconProps & React.RefAttributes<HTMLSpanElement>
  >;
  entitlement?: string;
  subMenus?: SubMenuItemInterface[];
  key?: string;
}

export interface MainMenuItemInterface {
  name: string;
  route: string;
  icon: React.ForwardRefExoticComponent<
    AntdIconProps & React.RefAttributes<HTMLSpanElement>
  >;
  key: string;
  entitlement?: string;
  subMenus?: SubMenuItemInterface[];
}

export interface Role {
  id: string;
  name: string;
  key?: string | number;
  align?: string;
  users: User[] | [];
}

export interface UserNotification {
  id: string;
  message: string;
  createdAt: string;
}

export interface APIKey {
  id: string;
  key: string;
  user?: User;
}

export interface SalesPerson {
  id: string;
  name: string;
  code: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  key?: string | number;
  align?: string;
  department: Department;
  role: Role;
  apiKeys: APIKey[];
  entitlements?: Entitlement[] | [];
  userNotifications?: UserNotification[] | [];
  customReports?: CustomReport[];
}

type CType =
  | "Employee Post Tax"
  | "Employee Pre Tax"
  | "Pre Tax Employee"
  | "Post Tax Employee"
  | "Employer Post Tax"
  | "Employer Pre Tax"
  | "Contribution";

export interface ContributionMapping {
  contributionType: CType;
  deal_contribution_type: string;
  amount: number;
  units: number;
}

export interface TaxDetailsType {
  totalAmount: number;
  totalUnits: number;
}
export interface MappingsSummaryType {
  employeePreTax: TaxDetailsType;
  employeePostTax: TaxDetailsType;
  employerPreTax: TaxDetailsType;
  employerPostTax: TaxDetailsType;
}
export interface Contribution {
  key?: string;
  id: string;
  client_name?: string;
  clientName: string;
  description: string;
  mappings: ContributionMapping[];
  priceDate: string;
  price_date: string;
  client_petraid: string;
  deal_description: string;
  deal_type: string;
  total_amount: number;
  total_units: number;
  totalAmount: number;
  totalUnits: number;
  dealDescription: string;
  dealType: string;
  transactionType: string;
  shareClass: string;
  customerPetraId: string;
  clientPetraId: string;
  Contribution?: number;
  ta_id?: string;
}

export interface ProductContribution {
  id?: string;
  balance: number;
  contributions: Contribution[];
  fundcode: string;
  productName: string;
  redeemableBalance: number;
  holdBalance?: number;
  contributionTypesTotal?: MappingsSummaryType;
}

export interface CustomerBalanceSummary {
  dateCreated: string;
  productLien: number;
  productName: string;
  redeemableBalance: number;
  totalBalance: number;
}

export interface ProductContributionSubscriptionMapping {
  clientContributions: Contribution[];
  subscription: Subscription;
  scheme: Scheme;
}

export type ModelTypes =
  | "Department"
  | "Role"
  | "User"
  | "CustomerSubscription"
  | undefined;

export interface Audit {
  id: string;
  auditableId: string;
  auditableType: ModelTypes;
  action: string;
  auditedChanges: {
    name: string | string[];
  };
  customerId?: string;
  user?: User;
  key?: string;
  createdAt: string;
}

export interface Entitlement {
  id: string;
  name: string;
  enabled?: boolean;
  key?: string | number;
  relatedModel?: string;
  entityExceptions: EntityException[];
}

export interface Permission {
  id: string;
  role: Role;
  entitlement: Entitlement;
  isActive: boolean;
}

export interface RouteConfig {
  path: string;
  exact: boolean;
  entitlement: string;
  requiresAuth: boolean;
  title: string;
  component: React.Component | React.FC;
}

export interface BuildAndReleaseSchema {
  apiVersion: string;
  environment: string;
  webVersion: string;
}

export interface RadioOptionsInterface {
  id: string;
  name: string;
  value: string;
}

export interface SelectOptionsInterface {
  id: string;
  name: string;
  value?: string | number;
  key?: string | number;
  isVerified?: boolean;
}

export interface RedemptionRequestChecklist {
  name: string;
  isRequired: boolean;
}

export interface Price {
  id: string;
  priceDate: string;
  amount: number;
  key?: string | number;
  isOpen?: boolean;
  createdAt?: string;
}

export interface Product {
  id: string;
  name: string;
  isActive?: boolean;
  productTier: SelectOptionsInterface;
  productCategory: SelectOptionsInterface;
  opportunityProspect?: SelectOptionsInterface;
  transactionFields: TransactionFields;
  fundCode?: string;
  taxWaiverRequired?: boolean;
  prices?: Price[];
  key?: string | number;
  assetMapId?: string;
  vanCode?: string;
  financialInstitution?: FinancialInstitution;
  syncPrice: boolean;
  subproducts?: Product[];
  developerName?: string;
  developerContact?: string;
  costOfProperty?: number;
  productFundingOption?: string;
  mortgageDetails?: string;
  specificationsOfProperty?: string;
  accruedBenefitDistribution?: string;
  accruedBenefitBalance?: number;
  fixedIncomeOffers?: FixedIncomeOffer[];
  productTemplates?: ProductTemplate[];
}

export interface ProductTemplate {
  id: string;
  template: Template;
  to: string;
  medium: string;
}

export type TransactionStatusType =
  | "reversed"
  | "redemption_approved"
  | "initiated"
  | "approved"
  | "pending"
  | "cancelled"
  | "failed"
  | "expired"
  | "issued"
  | "paid"
  | "hold"
  | "returned";

export interface CustomerTransaction {
  id: string;
  key?: string;
  description: string;
  provider: string;
  mode: string;
  priceDate: string;
  totalAmount: number;
  status: TransactionStatusType;
  transactionType: string;
  reference: string;
  engine: string;
  externalId: string;
  postTaxAmount: number;
  preTaxAmount: number;
  netAmount: number;
  initialRedemptionAmount: number;
  withholdingTax: number;
  withholdingTaxConfirmed?: boolean | null;
  statusReason: string;
  token: string;
  createdAt: string;
  completedAt?: string;
  customer: Customer;
  company: Company;
  customerSubscription: Subscription;
  taTransactionStatus?: string;
  customerTransactionNotes?: Note[];
  reasons?: Reason[];
  reasonComments?: string[];
  customerTransactionsReasons?: CustomerTransactionsReason[];
  redemptionRequestAttachments?: RedemptionRequestAttachment[];
  payoutAccount?: PaymentAccount;
  subCategory?: SubCategory;
  idType?: Id;
  idNumber?: string;
  contactInformation?: string;
  employmentDate?: string;
  exitDate?: string;
  lienAmount?: number;
  vestingPercentage?: number;
  redemptionRequestPurpose?: SelectOptionsInterface;
  redemptionRequestSubPurpose?: RedemptionRequestSubPurpose;
  redemptionValue?: number;
  redemptionType?: string;
  correctInfoDate?: string;
  redemptionPreference?: string;
  allocationAmountBreakdown?: RedemptionRequestAllocation;
  platformFee?: number;
  canTransitionToNextStates?: TransitionToNextState[];
  notifierRecords?: NotificationRecord[];
  source?: string;
  riskBreakdown?: any;
  dealProcessed: boolean;
  deal?: any;
}

export interface CustomerTransactionsReason {
  comment?: string;
  customerTransactionStatus: string;
  date?: string;
  id: string;
  reason: Reason;
  amount?: number;
}

export interface CustomerTransactionNote {
  id: string;
  createdAt: string;
  updatedAt: string;
  customerTransaction: CustomerTransaction;
  note: Note;
}

export interface TransactionFields {
  [key: string]: string;
}

export interface SubscriberListItem {
  id: string;
  user: User;
}

export interface Subscriber {
  id: string;
  name: string;
}

export interface FinancialInstitution extends SelectOptionsInterface {
  bankBranches: SelectOptionsInterface[];
  institutionType: string;
}
export interface PaginatedResource {
  count: number;
  errors: APIError;
  results: Customer[];
}

export interface CompanyContact {
  id: string;
  emailAddress: string;
  mobileNumber: string;
  contactName: string;
  contactLevels: ContactLevel[];
  company: Company;
  customer?: Customer;
  role?: string;
  documentUrl?: string;
}

export interface TagRecord {
  id: string;
  tag: SelectOptionsInterface;
  isActive: boolean;
  activatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CompanyClosedInterval {
  id: string;
  closedAt?: string;
  reopenedAt?: string;
  notes?: string;
  proofOfClosureDocumentUrls?: string[];
  proofOfReopeningDocumentUrls?: string[];
}

export interface Company {
  formerEmployees?: PaginatedResource;
  currentEmployees?: PaginatedResource;
  key?: string;
  companyPhones: AssociatedPhone[];
  companyAddresses: AssociatedAddress[];
  companyEmails: AssociatedEmail[];
  id: string;
  name: string;
  petraId: string;
  tinNumber: string;
  customers: Customer[];
  phones?: PhoneInterface[];
  emails?: EmailInterface[];
  customerCompanyAssociations: CustomerCompanyAssociation[];
  contactPersons: CustomerCompanyAssociation[];
  companyContacts?: CompanyContact[];
  authorizers?: CompanyContact[];
  opportunities?: Opportunity[];
  industrySector?: SelectOptionsInterface;
  trustee?: SelectOptionsInterface;
  numberOfEmployees: number;
  notifierRecords: NotificationRecord[];
  tags: SelectOptionsInterface[];
  exclusionListTags?: SelectOptionsInterface[];
  allCompanyTags?: TagRecord[];
  companyClosedIntervals?: CompanyClosedInterval[];
  companyActivities: Activity[];
  companyInteractions: Activity[];
  companySubscriptions?: Subscription[];
  redemptionRequests: RedemptionsRequest[];
  transferRequests: TransferRequest[];
  companyPayoutAccounts: CompanyPaymentAccount[];
  customerInteractions?: Activity[];
  port: Port;
  companySegment?: string;
  emailSearchValue?: string;
  phoneSearchValue?: string;
  contactPersonValue?: string;
  relationshipManager?: User;
  paginatedResource?: PaginatedResource;
  tickets?: Ticket[];
  mergedFrom?: Company[];
  mergedTo?: Company;
  merged?: boolean;
  companyMergeLogs?: CompanyMergeLog[];
  ssnitNumber?: string;
  rocNumber?: string;
  directors?: Director[];
  dateOfEstablishment?: string;
  fileUploads?: FileUpload;
  companyInfoFileUploads?: FileUpload[];
}

export interface FormFieldConfigInterface {
  id: string;
  ref: string;
  name?: string;
  type: string;
  ruleType?: string;
  autoSize?: { minRows: number; maxRows: number };
  placeholder?: string;
  disabledDate?: (current: Moment | undefined) => boolean;
  defaultPickerValue?: Moment;
  required: boolean | false;
  validationErrorMessage?: string;
  className?: string | undefined;
  label?: string | undefined;
  style?: Record<string, unknown> | undefined;
  colConfig?: Record<string, unknown>;
  disabled?: boolean | false;
  initialValue?:
    | string
    | number
    | Moment
    | boolean
    | string[]
    | number[]
    | undefined;
  selectOptions?: SelectOptionsInterface[];
  radioOptions?: RadioOptionsInterface[];
  radioChangeHandler?: (e: RadioChangeEvent) => void;
  changeHandler?: (value: string) => void;
  elementStyle?: React.CSSProperties;
  handleClick?: () => void;
  formItemStyle?: React.CSSProperties;
  tagWithOptGroupOptions?: { [x: string]: SelectOptionsInterface[] };
  isArrayField?: boolean;
}

export type InputTypes =
  | "checkbox"
  | "phone"
  | "string"
  | "email"
  | "date"
  | "number"
  | "defaultDate"
  | "select"
  | "optionalSelect"
  | "radio"
  | "button"
  | "buttonGroup"
  | "textArea"
  | "fileUpload"
  | "tagSelectWithOptGroup"
  | "description"
  | "alert"
  | "search"
  | "multipleSelect"
  | "multipleSelectSearch"
  | "numberArray"
  | "stringArray"
  | "phoneArray"
  | "emailArray"
  | "switch";

export interface ColConfig {
  offset?: number;
  span: number;
}

export interface FormFieldConfig {
  id?: string;
  name: string;
  value?: string;
  inputType: InputTypes;
  alertType?: "success" | "info" | "warning" | "error";
  rule?: RuleType | "array";
  autoSize?: { minRows: number; maxRows: number };
  placeholder?: string;
  disabledDate?: (current: Moment | undefined) => boolean;
  valuePropName?: string;
  defaultPickerValue?: Moment;
  required: boolean | false;
  errorMessage?: string;
  patternErrorMessage?: string;
  className?: string | undefined;
  label?: string | undefined;
  style?: Record<string, unknown> | undefined;
  colConfig?: ColConfig;
  disabled?: boolean | false;
  initialValue?:
    | string
    | number
    | Moment
    | boolean
    | string[]
    | number[]
    | undefined;
  selectOptions?: SelectOptionsInterface[];
  radioOptions?: RadioOptionsInterface[];
  buttonGroup?: Array<{
    label: string;
    onClick?: () => void;
    className?: string | undefined;
    type?: "primary" | "default" | "dashed" | "link" | "text";
    danger?: boolean;
    disabled?: boolean;
    loading?: boolean;
    tooltip?: string;
    style?: Record<string, unknown> | undefined;
  }>;
  dateOptions?: {
    limitTo18: boolean;
    startYear?: number;
    allowPastDates?: boolean;
  };
  radioChangeHandler?: (e: RadioChangeEvent) => void;
  checkboxChangeHandler?: (e: CheckboxChangeEvent) => void;
  checkboxCheckedValue?: boolean;
  changeHandler?: (value: string) => void;
  elementStyle?: React.CSSProperties;
  handleClick?: () => void;
  formItemStyle?: React.CSSProperties;
  defaultFileList?: UploadFile[];
  tagWithOptGroupOptions?: { [x: string]: SelectOptionsInterface[] };
  isArrayField?: boolean;
  dateChangeHandler?: (e: Moment | null) => void;
  fileUploadProps?: {
    type?: string;
    opportunityId?: string | null;
    companyId?: string | null;
    customerId?: string | null;
    isTriggeredManually?: boolean;
    toggleModal?: () => void;
    skipJobProcess?: boolean;
    accept?: string; // e.g., ".pdf,.doc,.docx" or "image/*,application/pdf"
    multiple?: boolean;
    onFilesChange?: (files: { url: string; name: string }[]) => void;
  };
  size?: SizeType;
  isSearching?: boolean;
  allowClear?: boolean;
  selectHandler?: (value: string, option: any) => void;
  searchHandler?: (value: string) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  clearHandler?: () => void;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  defaultValue?: string;
  onFileDelete?: (file: UploadFile) => void;
  onNumberChange?: ((value: 0) => void) | undefined;
  rules?: Rule[];
  pattern?: RegExp;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  step?: number;
  normalize?: (value: any) => any;
  min?: number;
  max?: number;
  onRemoveNumberArrayItem?: (index: number) => void;
  onAddNumberArrayItem?: () => void;
  onNumberArrayChange?: (index: number, value: number) => void;
  onStringArrayChange?: (index: number, value: string) => void;
  onAddStringArrayItem?: () => void;
  onRemoveStringArrayItem?: (index: number) => void;
  checkedChildren?: string;
  unCheckedChildren?: string;
  tooltip?: string;
  switchChangeHandler?: (checked: boolean) => void;
  dateFormat?: string;
}

export interface CustomerMergeLog {
  duplicateCustomer: Partial<Customer>;
  id?: string;
  mainCustomer: Partial<Customer>;
  createdAt?: string;
}

export interface CompanyMergeLog {
  duplicateCompany: Partial<Company>;
  id?: string;
  mainCompany: Partial<Company>;
  createdAt?: string;
}

export interface MaritalStatus {
  id: string;
  name: string;
}

export interface Title {
  id: string;
  name: string;
}

export interface UssdAccount {
  id: string;
  accountStatus: string;
  hashedPin: string;
  customer?: Customer;
  phone: PhoneInterface;
}

export interface MembersPortalAccount {
  id: string;
  username: string;
  registrationStatus: string;
}

export interface Customer {
  id: string;
  petraId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  gender: string | null;
  dateOfBirth: string | null;
  ssnitNumber: string;
  staffIds?: [string];
  merged: boolean;
  phones?: PhoneInterface[];
  emails?: EmailInterface[];
  addresses?: AddressInterface[];
  activeCustomerPhones: AssociatedPhone[];
  customerEmails: AssociatedEmail[];
  customerAddresses: AssociatedAddress[];
  customerMergeLogs?: CustomerMergeLog[];
  customerCompanyAssociations: CustomerCompanyAssociation[];
  companyAssociations: CustomerCompanyAssociation[];
  employers: Company[];
  opportunities: Opportunity[];
  monthlyContribution?: number;
  lumpDeposit?: number;
  contributionType?: string;
  country: {
    id: string;
    name: string;
  } | null;
  maritalStatus?: MaritalStatus;
  idType?: Id;
  photoIdUrl?: string;
  photoIdNumber?: string;
  ghanaCardNo?: string;
  ghanaCardIdUrl?: string;
  ghanaCardIdBackUrl?: string;
  customerDebitAccounts: PaymentAccount[];
  customerPayoutAccounts: PaymentAccount[];
  key?: string;
  signatureUrl?: string;
  notifierRecords: NotificationRecord[];
  tags: SelectOptionsInterface[];
  exclusionListTags?: SelectOptionsInterface[];
  allCustomerTags?: TagRecord[];
  customerActivities: Activity[];
  customerInteractions: Activity[];
  customerSubscriptions?: Subscription[];
  formerCompanies: Company[];
  redemptionRequests: [RedemptionsRequest];
  transferRequests: TransferRequest[];
  customerSegment?: string;
  selfServiceActivities?: SelfServiceActivity[];
  title?: Title;
  fullname: string;
  ussdAccount?: UssdAccount;
  memberPortalAccount?: MembersPortalAccount;
  emailSearchValue?: string;
  phoneSearchValue?: string;
  mergedTo?: Customer;
  mergedFrom?: Customer[];
  tickets?: Ticket[];
  idExpiryDate?: string;
  idIssueDate?: string;
  customerInfoFileUploads?: FileUpload[];
  customerBackgroundInformation?: CustomerBackgroundInformation;
  age?: number;
  ghanaCardVerifiedAt?: string;
  isGhanaCardVerified?: boolean;
  lastLoanRequestKyc?: LoanRequestKyc;
  lastFixedIncomeRequestKyc?: FixedIncomeRequestKyc;
  isDobVerified?: boolean;
  dobVerifiedAt?: string;
  verificationProofUrl?: string;
}

export interface CustomerBackgroundInformation {
  fatherName?: string;
  fatherDeceased?: boolean;
  fatherPhoneNumber?: string;
  motherName?: string;
  motherDeceased?: boolean;
  motherPhoneNumber?: string;
  previousSsnitNumber?: string;
  hometown?: string;
  nextOfKinName?: string;
  nextOfKinPhoneNumber?: string;
  previousEmployer?: string;
}

export interface PayoutAccount {
  id: string;
  name: string;
  accountNumber: string;
  accountType: string;
  bankBranch: BankBranch;
}

export interface PaymentAccount {
  id: string;
  name: string;
  institutionName?: string;
  accountNumber: string;
  accountType?: keyof PaymentAccountTypes;
  bankAccountType?: keyof BankAccountTypes;
  bankBranch?: BankBranch;
  customerSubscription?: {
    id: string;
    product: Product;
  };
  amount?: number;
  customer?: Customer;
  expirationDate?: string;
  isVerified?: boolean;
  authorizationFormUrl?: string;
  payoutAccount: PayoutAccount;
  isPayoutAccountVerified?: boolean;
  payoutAccountVerifiedAt?: string;
}

export interface BankBranch {
  id: string;
  name: string;
  financialInstitution: FinancialInstitution;
  sortCode?: string;
}

export interface CompanyPaymentAccount extends PaymentAccount {
  company: Company;
}

export interface Beneficiary {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  idType?: Id;
  idNumber?: string;
  dateOfBirth?: string;
  benefitsPercentage?: number;
  beneficiaryAmount?: number;
  emailAddress?: string;
  phoneNumber: string;
  customerRelationship: SelectOptionsInterface;
  customerSubscription?: Subscription;
  guardianName?: string;
  guardianPhoneNumber?: string;
  guardianEmail?: string;
  guardianAddress?: string;
  beneficiaryType?: string;
  institutionAddress?: string;
  institutionName?: string;
  institutionPhoneNumber?: string;
  title?: Title;
  fullname: string;
  isActive?: boolean;
  deletedAt?: string;
  ghanaCardBackUrl?: string;
  ghanaCardUrl?: string;
  idIssueDate?: string;
  idExpiryDate?: string;
}

export interface OpportunityProspect {
  id: string;
  name: string;
  products: Product[];
}

export interface InteractionMode {
  id: string;
  name: string;
}

export interface CustomerCompanyAssociationContactLevel {
  id: string;
  contactLevel: ContactLevel;
}

export interface CustomerCompanyAssociation {
  id: string;
  relationship: string;
  influenceLevel: string;
  customer: Customer;
  company: Company;
  isEmployee: boolean;
  dateOfEmployment: string;
  customerCompanyAssociationContactLevels: CustomerCompanyAssociationContactLevel[];
  staffId?: string;
  isActive?: boolean;
  natureOfEmployment?: "Hazardous" | "Non-Hazardous";
  frequencyOfIncome?: "Daily" | "Twice a Month" | "Monthly";
}

export interface TransferRequestNote {
  note: Note;
  transferRequest: TransferRequest;
}

export interface RedemptionRequestNote {
  id: string;
  note: Note;
  redemptionsRequest: RedemptionsRequest;
  redemptionRequestNoteTickets?: NoteTicket[];
}

export interface RedemptionRequestSubPurpose {
  id: string;
  redemptionRequestPurpose?: SelectOptionsInterface;
  redemptionRequestSubPurposeItem: SelectOptionsInterface;
}
export interface DownloadableTemplate {
  id: string;
  name: string;
  url: string;
}

export interface TaxContribution {
  employer_post_tax?: number;
  employee_post_tax?: number;
  employer_pre_tax?: number;
  employee_pre_tax?: number;
  contribution?: number;
}

export interface VestedPercentage {
  source?: string;
  parsedValue?: number;
  parsed_value?: number;
}

export interface RedemptionRequestAllocation {
  amount?: number;
  customer_contributions_deductions?: TaxContribution;
  customer_contributions_units_deductions?: TaxContribution;
  error?: string | null;
  previous_customer_contributions?: TaxContribution;
  vested_percentage?: VestedPercentage;
  unvested_allocations?: TaxContribution;
}

export interface ContactLevel {
  id: string;
  level: string;
  name: string;
}

export interface Ticket {
  id: string;
  properties: Record<string, string>;
  reference: string;
  source: string;
  url: string;
  number: string;
  state: string
}

export interface NoteTicket {
  id: string;
  ticket: Ticket;
}

export interface Note {
  id: string;
  userId: string;
  description: string;
  key?: string;
  createdAt: string;
  user: User;
  tickets?: NoteTicket[];
  referenceId?: string;
  updatedAt?: string;
  note?: Note
}
export interface CompanyActivity {
  id: string;
  activityDate: string;
  description: string;
  userId: string;
  companyId: string;
  key?: string;
  createdAt: string;
  activityMode: ActivityMode;
}

export interface CustomerActivity {
  id: string;
  activityDate: string;
  description: string;
  userId: string;
  customerId: string;
  key?: string;
  createdAt: string;
  activityMode: ActivityMode;
}

export interface CustomerActivity {
  id: string;
  activityDate: string;
  description: string;
  userId: string;
  customerId: string;
  key?: string;
  createdAt: string;
}

export interface OpportunityInteraction {
  id: string;
  key?: string | number;
  description: string;
  interactionDate?: string;
  interactionMode: InteractionMode;
  createdAt: string;
  createdBy?: {
    id: string;
  };
}

export interface UploadedFile {
  id: string;
  url: string;
  name?: string;
  key?: string | number;
}

export interface TransferRequestAttachment {
  id: string;
  attachment: Attachment;
  transferRequest: TransferRequest;
}

export interface RedemptionRequestAttachment {
  id: string;
  attachment: Attachment;
  redemptionsRequest: RedemptionsRequest;
}

export interface TemplateAttachment {
  id: string;
  attachment: Attachment;
}

export interface TemplateRecipientContactLevel {
  id: string;
  contactLevel: ContactLevel;
}

export interface CustomerInteractionAttachment {
  id: string;
  attachment: Attachment;
  customerInteraction?: Activity;
}

export interface CompanyInteractionAttachment {
  id: string;
  attachment: Attachment;
  companyInteraction?: Activity;
}

export interface InteractionTicket {
  id: string;
  ticket: Ticket;
  customerInteraction?: Activity;
  companyInteraction?: Activity;
}

export interface Attachment {
  id: string;
  url: string;
  key?: string;
  name?: string;
}

export interface Report {
  id: string;
  name: string;
  query: string;
  parameters?: Record<string, string>;
}

export interface CustomReport {
  id: string;
  name: string;
  parameterValues: Record<string, string>;
  report: Report;
}

export interface ReportData {
  columns: string[];
  data: string[];
}

export interface Liens {
  id: string;
  amount: number;
  customerSubscription?: Subscription;
  companySubscription?: Subscription;
}

export interface SubscriptionTag {
  id: string;
  tag: SelectOptionsInterface;
}

export interface VirtualAccountNumber {
  financialInstitution: FinancialInstitution;
  id: string;
  van: string;
  oldVan?: string;
}

export interface Subscription {
  id: string;
  product: Product;
  customer?: Customer;
  company?: Company;
  createdAt: string;
  productDuration?: string;
  firstContributionDate?: string;
  firstTierContributionDate?: string;
  computedFirstContributionDate?: string;
  firstSchemeContributionDate?: string;
  proofOfContributionDocumentUrl?: string;
  customerBeneficiaries: Beneficiary[];
  customerSubscriptionTags: SubscriptionTag[];
  companySubscriptionTags: SubscriptionTag[];
  customerLiens: CustomerLien[];
  companyLiens: Liens[];
  customerVan?: VirtualAccountNumber;
  companyVan?: VirtualAccountNumber;
  vestingRule?: VestingRule;
  customerVestingRules?: CustomerVestingRule[];
  customerPayoutAccounts?: PaymentAccount[];
  salesPerson?: SalesPerson;
  updateComputedFirstContributionDate?: string;
  contributionAllocationTypes?: string[];
  brokerageProductId?: string;
}

export interface Opportunity {
  id: string | null;
  state: string;
  companyQuery?: string;
  progressStatus: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: { name: string };
  opportunityType: string;
  customer?: Customer;
  company?: Company;
  signupReason?: string;
  sourceOfAwareness?: string;
  newEntity: boolean | null;
  opportunityProspect: OpportunityProspect | null;
  opportunitySource: SelectOptionsInterface | null;
  referrer?: SelectOptionsInterface;
  product?: Product;
  opportunityInteractions: OpportunityInteraction[];
  assignee?: User;
  contributionType?: keyof ContributionTypes;
  contributionAllocationTypes?: string[];
  monthlyExpectedContribution?: number;
  lumpsumExpectedContribution?: number;
  key?: string | null;
  directDebitAuthorizationForm?: FileUpload;
  signedApplicationForm?: FileUpload;
  customerSubscriptions?: Subscription[];
  companySubscription?: { contributionAllocationTypes?: string[] };
  productPaymentType?: keyof ProductPaymentType;
  employeeDatasheet?: FileUpload;
  npraForm?: FileUpload;
  vestingRuleDocument?: FileUpload;
  preOpportunityInteractions?: PreOpportunityInteraction[];
  employerId?: string;
  salesPerson?: User;
  customerInfoFileUploads?: FileUpload[];
  notifierRecords?: NotificationRecord[];
}

export interface ProductSchedule {
  name: string;
  tier: string;
}

export interface Schedule {
  id: number;
  contributionType: string;
  contributionDates: string[];
  tier: string;
  isValid: boolean;
  createdAt: string;
  matchProcessedAt: string;
  totalAmount: number;
  totalSalary: number;
  taId: String;
  scheme?: string;
}

export interface Payment {
  id: number;
  taId?: string;
  contributions: Record<string, unknown>[];
  transactionDate: string;
  valueDate: string;
  transactionAmount: number;
  scheme?: string;
  returnedAt?: string | null;
  returnChequeNumber?: string | null;
  returnPaymentType?: string | null;
  returnedChequeNotified?: boolean;
}

export interface AddressInterface {
  id: string;
  streetName: string;
  poBox: string;
  city: string;
  country: SelectOptionsInterface;
  isPostalBox: boolean;
  ghPost?: string;
  region: string;
}

export interface EmailInterface {
  id: string;
  address: string;
}

export interface PhoneInterface {
  id: string;
  number: string;
  type: string;
}

export interface ObjectLiteral {
  [key: string]: string;
}

export interface AssociatedPhone {
  id: string;
  phone: PhoneInterface;
  isPrimary: boolean;
  isActive?: boolean;
}

export interface AssociatedEmail {
  id: string;
  email: EmailInterface | null;
  isPrimary: boolean;
  isActive?: boolean;
}

export interface AssociatedAddress {
  id: string;
  address: AddressInterface;
  isPrimary: boolean;
  isActive?: boolean;
  company?: Company;
}

export interface AUMTarget {
  id: string;
  key?: string | number;
  amount: number;
  role: Role;
}

export interface Template {
  id: string;
  key: string | number;
  body: string;
  templateType: string;
  name: string;
  emailSubject: string;
  templateCategory?: TemplateCategory;
  templateAttachments: TemplateAttachment[];
  recipientTemplateContactLevels: TemplateRecipientContactLevel[];
  recipientTemplateCcContactLevels: TemplateRecipientContactLevel[];
}

export interface TemplateCategory {
  id: string;
  key?: string | number;
  name: string;
  description?: string;
}

export interface Holiday {
  id: string;
  key?: string | number;
  title: string;
  eventDate: string;
  recurring: boolean;
  isActive: boolean;
}

export type InteractionClosureType = "inbound" | "outbound";

export interface CloseInteractionPayload {
  closureType?: InteractionClosureType;
  templateId?: string;
  transferRequestId?: number;
  sendSurvey?: boolean;
  customComment?: string;
  recipientOverride?: string;
}


export interface InteractionNotificationPreview {
  templateName: string;
  templateType: string;
  notificationType: string;
  recipient: string;
  ccRecipients?: string[];
  subject?: string;
  body: string;

}



export interface ProductCategory {
  id: string;
  key?: string | number;
  name: string;
  products: Product[];
}

export enum FileTypes {
  signedApplicationForm = "signed_application_form",
  directDebitAuthorizationForm = "direct_debit_authorization_form",
  employeeDatasheet = "bulk_upload_employee_datasheet",
  npraForm = "npra_form",
  vestingRuleDocument = "vesting_rule_document",
  users = "bulk_upload_users",
  loanApplicationFormTemplate = "loan_application_form_template",
}

export enum Greetings {
  morning = "Good Morning",
  afternoon = "Good Afternoon",
  evening = "Good Evening",
}

export enum DocumentTypes {
  npraForm = "NPRA Form",
  signedApplicationForm = "Signed Employer Application Form",
  eds = "Employee Data Sheet",
  vestingRuleDocument = "Vesting Rule Document",
}

export interface Country {
  id: string;
  name: string;
  flagUrl: string;
  isoCode: string;
  phoneCode: string;
}

export enum OpportunityProspectTypes {
  retailClient = "Individual",
  institutionalClient = "Company",
}

export interface UpdateCustomerAddressFnProps {
  customerAddress: AssociatedAddress;
  newlyCreatedAddressId: string;
}

export interface UpdateCompanyAddressFnProps {
  companyAddress: AssociatedAddress;
  newlyCreatedAddressId: string;
}

export interface UpdateCustomerEmailFnProps {
  customerEmail: AssociatedEmail;
  newlyCreatedEmailId: string;
}

export interface UpdateCompanyEmailFnProps {
  companyEmail: AssociatedEmail;
  newlyCreatedEmailId: string;
}

export interface UpdateCustomerPhoneFnProps {
  customerPhone: AssociatedPhone;
  newlyCreatedPhoneId: string;
}

export interface UpdateCompanyPhoneFnProps {
  companyPhone: AssociatedPhone;
  newlyCreatedPhoneId: string;
}

export interface GetUserTokenResponse {
  user: User | null;
  token: string | null;
}

export interface APIError {
  property: string;
  description: string;
}

export interface UpdateDocumentsInformationFnProps {
  directDebitAuthorizationForm?: FileUpload;
  signedApplicationForm?: FileUpload;
  npraForm?: FileUpload;
  employeeDatasheet?: FileUpload;
  vestingRuleDocument?: FileUpload;
  companyInfoFileUploads?: FileUpload[];
  customerInfoFileUploads?: FileUpload[];
}

export interface UpdatePaymentInformationFnProps {
  customerPayoutAccounts?: PaymentAccount[];
  customerDebitAccounts?: PaymentAccount[];
  companyPayoutAccounts?: CompanyPaymentAccount[];
}

export interface UpdateBeneficiaryInformationFnProps {
  beneficiaries: Beneficiary[];
  subscriptionId: string;
  previousSubscriptionId?: string;
  beneficiaryId?: string;
}

export interface UpdateCustomerCompanyAssociationFnProps {
  associations: CustomerCompanyAssociation[];
}

export interface ValidationStatus {
  message: string;
  status: boolean;
}

export interface NotificationType {
  email: string;
  sms: string;
}

export interface NotificationStatusTypes {
  delivered: string;
  failed: string;
}

export interface NotificationCategoryTypes {
  onboarding: string;
  transfer_requests: string;
}

export interface ProductPaymentType {
  direct_debit: string;

  non_direct_debit: string;

  pre_tax: string;

  employer: string;

  post_tax: string;

  pre_and_post_tax: string;
}

export interface Id {
  id: string;
  name: string;
}

export interface InfluenceLevelTypes {
  low: string;
  medium: string;
  high: string;
}
export interface BeneficiaryTypes {
  institution: string;

  individual: string;

  minor: string;
}

export interface ProductWithSubscriptionId extends Product {
  subscriptionId: string;
  productDuration?: string;
  customerPayoutAccounts?: PaymentAccount[];
}

export interface CompanyWithCustomerCompanyAssociationId extends Company {
  customerCompanyAssociationId: string;
}

export interface BankAccountTypes {
  savings_account: string;

  current_account: string;
}

export interface PaymentAccountTypes {
  cheque: string;
  mm_account: string;
  bank_account: string;
}

export interface PaymentModeTypes {
  cheque: string;
  bank_transfer: string;
  mobile_money: string;
}

export interface ContributionTypes {
  lump_sum: string;
  monthly_contribution: string;
  monthly_and_lumpsum: string;
}

export interface TiersTypes {
  tier_2: string;
  tier_3: string;
  tier_4: string;
}

export interface TransferTypes {
  transfer_in: string;
  transfer_out: string;
  transfer_within: string;
  transfer_to_preservation: string;
  transfer_to_ezhome: string;
}

export interface ContactTypes {
  phone: string;
  email: string;
}
export interface ComplaintTypes {
  notAvailable: string;
  service: string;
  intermediary: string;
  product: string;
}

export interface RedemptionTypes {
  percentage: string;
  amount: string;
}

export interface TransferRequestStates {
  pending: string;
  cancelled: string;
  awaiting_funds: string;
  completed: string;
  payments: string;
  dealing: string;
  information_received: string;
  hold: string;
  correspondence: string;
  approved: string;
  paid: string;
  uploaded: string;
  reversed: string;
  returned: string;
  rejected: string;
  update_customer: string;
}

export interface RedemptionRequestStates {
  cancelled: string;
  pending: string;
  pending_vesting_confirmation: string;
  payments: string;
  dealing: string;
  hold: string;
  correspondence: string;
  approved: string;
  paid: string;
  returned: string;
  reversed: string;
  pending_authorizer: string;
  authorizer_cancelled: string;
  recalled: string;
  reissued: string;
  credited: string;
}

export interface CustomerTransactionStates {
  initiated: string;
  pending: string;
  expired: string;
  failed: string;
  issued: string;
  paid: string;
  reversed: string;
  redemptionApproved: string;
  approved: string;
  cancelled: string;
}

export interface Attachments {
  id: string;
  name: string;
  url: string;
}

export interface ExceptionTypes {
  inclusion: string;
  exclusion: string;
}

export enum NotificationStatusTypesEnum {
  delivered = "delivered",
  failed = "failed",
  pending = "pending",
  queued = "queued",
}

export enum NotificationTypesEnum {
  sms = "sms",
  email = "email",
}

export enum NotificationCategoryTypesEnum {
  onboarding = "onboarding",
  transfer_requests = "transfer_requests",
}

export enum BulkUploadStatusTypesEnum {
  completed = "completed",
  unprocessed = "unprocessed",
  inactive = "inactive",
  failed = "failed",
}

export enum BulkUploadSourceTypesEnum {
  phoenix = "phoenix",
  members_portal = "members_portal",
  sb_portal = "sb_portal",
  ussd = "ussd",
  unknown = "unknown",
}

export enum ExceptionTypesEnum {
  inclusion = "inclusion",
  exclusion = "exclusion",
}

export enum BankAccountTypesEnum {
  savingsAccount = "savings_account",
  currentAccount = "current_account",
}

export enum PaymentAccountTypesEnum {
  bankAccount = "bank_account",
  momoAccount = "mm_account",
  cheque = "cheque",
}

export enum PaymentModeTypesEnum {
  bankTransfer = "bank_transfer",
  cheque = "cheque",
  mobileMoney = "mobile_money",
}

export enum BeneficiaryTypesEnum {
  institution = "institution",
  individual = "individual",
  minor = "minor",
}

export enum InfluenceLevelTypesEnum {
  low = "low",
  medium = "medium",
  high = "high",
}

export enum TiersEnum {
  tier2 = "1",
  tier3 = "2",
  tier4 = "3",
}

export enum UssdAccountStatusTypeEnum {
  inactive = "inactive",
  active = "active",
  blocked = "blocked",
}

export enum UssdAccountStatusEnum {
  inactive = 0,
  active = 1,
  blocked = 2,
}

export enum PaymentTypesEnum {
  directDebit = "direct_debit",
  nonDirectDebit = "non_direct_debit",
  preTax = "pre_tax",
  postTax = "post_tax",
  employer = "employer",
  preAndPostTax = "pre_and_post_tax",
}

export enum ContributionTypesEnum {
  lumpSum = "lump_sum",
  monthlyContribution = "monthly_contribution",
  monthlyAndLump = "monthly_and_lumpsum",
}

export enum TransferTypeEnum {
  transferIn = "transfer_in",
  transferOut = "transfer_out",
  transferWithin = "transfer_within",
  transferToPreservation = "transfer_to_preservation",
  transferToEzhome = "transfer_to_ezhome",
}

export enum ContactTypesEnum {
  phone = "phone",
  email = "email",
}

export enum ComplaintsTypesEnum {
  notAvailable = "n/a",
  service = "service",
  intermediary = "intermediary",
  product = "product",
}

export enum RedemptionTypeEnum {
  percentage = "percentage",
  amount = "amount",
}

export enum RedemptionStateEnum {
  in_progress = "in_progress",
  completed = "completed",
  pending = "pending",
  cancelled = "cancelled",
}

export enum CompanySegmentsEnum {
  strategic = "strategic",
  growth = "growth",
  maintenance = "maintenance",
  niche = "niche",
  red = "red",
}

export const TransactionFieldsEnum = {
  employee_pre_tax: "Employee Pre-Tax",
  employee_post_tax: "Employee Post-Tax",
  employer_post_tax: "Employer Post-Tax",
  employer_pre_tax: "Employer Pre-Tax",
  contribution: "Contribution",
};

export type BranchMap = Record<string, SelectOptionsInterface[]>;

export interface EntityException {
  id: string;
  entityId: string;
  entitlement: Entitlement;
  user: User;
  exceptionType: string;
  key?: string | number;
}

export interface Entity {
  id: string;
  name: string;
  petraId?: string;
}

export enum PluralEntities {
  users = "users",
  customers = "customers",
  companies = "companies",
}

export enum Entities {
  user = "user",
  customer = "customer",
  company = "company",
}

export interface MultipleChangelog {
  audits: Changelog[];
  stateAudits: Changelog[];
}

export interface Changelog {
  key: string;
  auditItem: string;
  createdAt: string;
  action: string;
  newValue: string;
  oldValue: string;
  user: string;
  comment: string;
}

export interface SyncLog {
  additionalInfo: Record<string, string>;
  status: boolean;
  errorMessage?: string | null;
  syncedAt: string;
}

export interface SelfServiceActivity {
  id: string;
  channel: string;
  customer: Customer;
  description: string;
  category?: string;
  subCategory?: string;
  updatedAt: string;
}

export interface CheckboxConfig {
  label: string;
  value: string;
}

export interface NotificationRecord {
  key: string;
  id: string;
  message: string;
  recipient: string;
  notificationType: string;
  status: string;
  reference: string;
  createdAt: string;
  updatedAt: string;
  rejectReason?: string;
  customer?: Customer;
  company?: Company;
  trustee?: Trustee;
  details: string;
  additionalData?: Record<string, any>;
  redemptionRequest?: RedemptionsRequest;
  transferRequest?: TransferRequest;
  customerTransaction?: CustomerTransaction;
  opportunity?: Opportunity;
  attachments?: { name: string; type: string; content: string }[];
}

export interface LayoutConfig {
  layout: "horizontal" | "vertical" | undefined;
  mainContainer: ColConfig;
  logContainer: ColConfig;
}

export interface RedemptionRequestFollower {
  id: string;
  user: User;
  redemptionRequest?: RedemptionsRequest;
}

export interface SubCategory {
  id: string;
  name: string;
  longName?: string;
  isActive?: boolean;
  autoCloseable?: boolean;
  sla: number;
  slaCreateByEod?: number;
  category?: Category;
}

export interface Category {
  id: string;
  name: string;
  department: Department;
  subCategories: SelectOptionsInterface[];
  isActive: Boolean;
}

export interface TransferRequest {
  id: string;
  requestorType?: string;
  requestDate?: string;
  awaitingFundsDate?: string;
  awaitingFundsDaysCount?: number;
  transferType: keyof TransferTypes;
  fromProduct?: Product;
  contactInformation?: string;
  toProduct?: Product;
  authorizationFormUrl?: string;
  identificationFormUrl?: string;
  company?: Company;
  fromCompany?: Company;
  toCompany?: Company;
  customer?: Customer;
  trusteeScheme?: TrusteeScheme;
  createdAt: string;
  updatedAt: string;
  user: User;
  key?: string | number;
  transferStatus: keyof TransferRequestStates;
  transferRequestNotes: TransferRequestNote[];
  transferRequestAttachments: TransferRequestAttachment[];
  customerInteraction: Activity;
  transferRequestReason?: RequestReason;
  idType: Id;
  idNumber: string;
  idUrl: string;
  amount?: number;
  subCategory: SelectOptionsInterface;
  payoutAccount?: PaymentAccount;
  formerClient?: string;
  dueDate?: string;
  fromCompanyName?: string;
  toCompanyName?: string;
  ticketLinks?: string[];
  redemptionRequest?: RedemptionsRequest;
  processingDays?: number;
  canTransitionToNextStates?: TransitionToNextState[];
  notifierRecords?: NotificationRecord[];
  firstContributionDate?: string;
  proofOfContributionDocumentUrl?: string;
  taxWaiverNpraUrl?: string;
}

export interface BulkUpload {
  id: string;
  fileType: string;
  state: string;
  user: { name: string };
  source: string;
  createdAt: string;
  url: string;
  errorFileUrl?: string;
}

export interface ExportField {
  id: string;
  fields: JSON;
  model: string;
  name: string;
  user: User;
}

export interface ExportedDataStatus {
  completed: string;
  unprocessed: string;
  processing: string;
  failed: string;
}

export interface ExportedData {
  id: string;
  name: string;
  status: keyof ExportedDataStatus;
  url?: string;
  failureReason?: string;
  user: User;
  createdAt: string;
  exportField: ExportField;
}

export interface QprReport {
  id: string;
  name: string;
  status: keyof ExportedDataStatus;
  url?: string;
  createdAt: string;
  notificationSent: boolean;
}

export interface RedemptionsRequest {
  id: string;
  redemptionType: keyof RedemptionTypes;
  redemptionValue: number;
  redemptionAmount: number;
  initialRedemptionAmount: number;
  contactInformation?: string;
  externalReference?: string;
  unvestedExternalReference?: string;
  expectsUnvestedDeal?: boolean;
  isDeceased?: boolean;
  requestDate: string;
  valueDate?: string;
  customerPayoutAccount?: PaymentAccount;
  payoutAccount?: PaymentAccount;
  customerSubscription?: Subscription;
  company?: Company;
  customer?: Customer;
  companySubscription?: Subscription;
  companyPayoutAccount?: PaymentAccount;
  state: keyof RedemptionRequestStates;
  redemptionRequestNotes: RedemptionRequestNote[];
  redemptionRequestAttachments: RedemptionRequestAttachment[];
  redemptionRequestReason?: RequestReason;
  redemptionRequestFollowers?: RedemptionRequestFollower[];
  redemptionRequestPurpose?: SelectOptionsInterface;
  redemptionRequestSubPurpose?: RedemptionRequestSubPurpose;
  totalAmountType?: string;
  idType: Id;
  idNumber: string;
  idUrl: string;
  vestingValueType?: string;
  vestingValue?: string;
  vestingRuleUrl?: string;
  taxWaiverNpraUrl?: string;
  authorizer?: CompanyContact;
  source: string;
  transferRequest?: TransferRequest;
  batchNumber: number;
  subCategory: SelectOptionsInterface;
  user?: User;
  withholdingTax?: number;
  dueDate?: string;
  paidDate?: string;
  correctInfoDate?: string;
  amountPaid?: number;
  createdAt: string;
  updatedAt: string;
  paymentMode: string;
  unvestedUnassigned?: number;
  isManualAllocation?: boolean;
  manualContribution?: number;
  manualEmployerPreTax?: number;
  manualEmployerPostTax?: number;
  manualEmployeePreTax?: number;
  manualEmployeePostTax?: number;
  manualWithholdingTax?: number;
  isFormerCompany?: boolean;
  additionalData?: Record<string, string>;
  taTransactionStatus?: string;
  allocationAmountBreakdown?: RedemptionRequestAllocation;
  platformFee?: number;
  canTransitionToNextStates?: TransitionToNextState[];
  correctValueDate?: string;
  notifierRecords?: NotificationRecord[];
  notifyCustomer?: boolean;
  notifyClient?: boolean;
  initiator?: string;
  clientContactInformation?: string;
  unvestedEmployerPostTax?: number;
  unvestedEmployerPreTax?: number;
  waiveOffTax?: boolean;
  riskBreakdown?: any;
  dealProcessed: boolean;
  deal?: any;
  unvestedDeal?: any;
  finalAllocationAmount?: number;
  redemptionUnits?: number;
  finalEmployerPostTax?: number;
  finalEmployerPreTax?: number;
  finalEmployeePostTax?: number;
  finalEmployeePreTax?: number;
  finalContribution?: number;
}

export interface Reason {
  id: string;
  reasonDescription: string;
  reasonKey: string;
  redemptionRequests?: RedemptionsRequest[];
  transferRequests?: TransferRequest[];
}

export interface RequestReason {
  id: string;
  transferRequest?: TransferRequest;
  redemptionRequest?: RedemptionsRequest;
  fixedIncomeRequest?: FixedIncomeRequest;
  state: string;
  reason: Reason;
}

export interface InteractionSurveyOutcome {
  notificationPurpose?: string;
  customerId?: number;
  customerPetraId?: string;
  interactionId?: number;
  interactionCategory?: string;
  interactionCategoryLabel?: string;
  deliveryChannel?: string;
  deliveryChannelLabel?: string;
  sourceContactInformation?: string;
  sourceContactUsed?: string;
  notificationType?: string;
  subject?: string;
  templateId?: string | number;
  templateName?: string;
  templateKey?: string;
  templateType?: string;
  status?: string;
  details?: string;
  deliveryAttempted?: boolean;
  recordedAt?: string;
  reason?: string;
  errorClass?: string;
  errorMessage?: string;
  sendSurveyRequested?: boolean;
  matchedTemplateIds?: Array<string | number>;
  matchedTemplateNames?: string[];
  matchedTemplateKeys?: string[];
  matchedTemplateCategories?: string[];
  matchedTemplates?: string[];
}

export interface InteractionClosureMetadata {
  sendSurvey?: boolean;
  transferRequestId?: number;
  templateId?: string | number;
  templateName?: string;
  templateType?: string;
  survey?: InteractionSurveyOutcome;
}

export function normalizeInteractionSurveyOutcome(
  outcome?: Record<string, any> | null,
): InteractionSurveyOutcome | undefined {
  if (!outcome) {
    return undefined;
  }

  return {
    notificationPurpose:
      outcome.notificationPurpose ?? outcome.notification_purpose,
    customerId: outcome.customerId ?? outcome.customer_id,
    customerPetraId: outcome.customerPetraId ?? outcome.customer_petra_id,
    interactionId: outcome.interactionId ?? outcome.interaction_id,
    interactionCategory:
      outcome.interactionCategory ?? outcome.interaction_category,
    interactionCategoryLabel:
      outcome.interactionCategoryLabel ?? outcome.interaction_category_label,
    deliveryChannel: outcome.deliveryChannel ?? outcome.delivery_channel,
    deliveryChannelLabel:
      outcome.deliveryChannelLabel ?? outcome.delivery_channel_label,
    sourceContactInformation:
      outcome.sourceContactInformation ?? outcome.source_contact_information,
    sourceContactUsed: outcome.sourceContactUsed ?? outcome.source_contact_used,
    notificationType: outcome.notificationType ?? outcome.notification_type,
    subject: outcome.subject,
    templateId: outcome.templateId ?? outcome.template_id,
    templateName: outcome.templateName ?? outcome.template_name,
    templateKey: outcome.templateKey ?? outcome.template_key,
    templateType: outcome.templateType ?? outcome.template_type,
    status: outcome.status,
    details: outcome.details,
    deliveryAttempted:
      outcome.deliveryAttempted ?? outcome.delivery_attempted,
    recordedAt: outcome.recordedAt ?? outcome.recorded_at,
    reason: outcome.reason,
    errorClass: outcome.errorClass ?? outcome.error_class,
    errorMessage: outcome.errorMessage ?? outcome.error_message,
    sendSurveyRequested:
      outcome.sendSurveyRequested ?? outcome.send_survey_requested,
    matchedTemplateIds:
      outcome.matchedTemplateIds ?? outcome.matched_template_ids,
    matchedTemplateNames:
      outcome.matchedTemplateNames ?? outcome.matched_template_names,
    matchedTemplateKeys:
      outcome.matchedTemplateKeys ?? outcome.matched_template_keys,
    matchedTemplateCategories:
      outcome.matchedTemplateCategories ?? outcome.matched_template_categories,
    matchedTemplates: outcome.matchedTemplates ?? outcome.matched_templates,
  };
}

export function normalizeInteractionClosureMetadata(
  metadata?: Record<string, any> | null,
): InteractionClosureMetadata | undefined {
  if (!metadata) {
    return undefined;
  }

  return {
    sendSurvey: metadata.sendSurvey ?? metadata.send_survey,
    transferRequestId: metadata.transferRequestId ?? metadata.transfer_request_id,
    templateId: metadata.templateId ?? metadata.template_id,
    templateName: metadata.templateName ?? metadata.template_name,
    templateType: metadata.templateType ?? metadata.template_type,
    survey: normalizeInteractionSurveyOutcome(metadata.survey),
  };
}

export interface Activity {
  dueDate: string;
  id: string;
  key?: string;
  userId: string;
  createdAt: string;
  description: string;
  activityDate: string;
  revertDate: string;
  status: string;
  subCategory: SubCategory;
  activityLabel: SelectOptionsInterface;
  interactionSource: SelectOptionsInterface;
  interactionCategory?: string;
  sourceContactInformation?: string;
  customerInteractionAttachments?: CustomerInteractionAttachment[];
  companyInteractionAttachments?: CompanyInteractionAttachment[];
  preferredCommMode?: string;
  complaintType?: string;
  desiredOutcome?: string;
  contactInformation?: string;
  notes: Note[];
  user?: User;
  customerInteractionTickets?: InteractionTicket[];
  companyInteractionTickets?: InteractionTicket[];
  customer?: Customer;
  company?: Company;
  comment?: string;
  closedAt?: string;
  closedBy?: string;
  closureType?: InteractionClosureType;
  closureMetadata?: InteractionClosureMetadata;
}

export interface ActivityMode {
  id: string;
  name: string;
}

export enum RMCompanyActivityStatus {
  completed = "completed",
  pending = "pending",
}

export interface RMCompanyActivity {
  id: string;
  service: SelectOptionsInterface;
  category: Category;
  subCategory: SelectOptionsInterface;
  companyActivityType: SelectOptionsInterface;
  date: string;
  summary: string;
  assignee: User;
  team: Team;
  completed: boolean;
  completedOn?: string;
  status: RMCompanyActivityStatus;
  company?: Company;
}

export interface Lien {
  id: string;
  amount: number;
}

export interface CustomerLien {
  id: string;
  currentAmount: number;
  totalAmount: number;
  loanPaid?: boolean;
  employeePreTax: number;
  employeePostTax: number;
  employerPreTax: number;
  employerPostTax: number;
  provider?: string;
  startDate?: string;
  endDate?: string;
  customerSubscription: Subscription;
  lienAttachmentUrl: string;
  changeLogs?: Changelog[];
  company?: Company;
  loanRequest?: LoanRequest;
}

export interface FileOption {
  key: string;
  value: string;
}
export interface ChangeRequest {
  action: string;
  approvedBy: User;
  entityId: string;
  isApproved: boolean;
  requestedBy: User;
  key?: string;
  entityType: string;
  proposedChanges: string;
  petraId: string;
}

export interface TrusteeContactPerson {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  emailsInCopy?: string[];
  activeTrusteeSchemesCount?: number;
}

export interface TrusteeScheme {
  id: string;
  name: string;
  trusteeContactPerson?: TrusteeContactPerson;
  trustee: Trustee;
}

export interface TrusteeCompanySubscriptionVan {
  id: string;
  van: string;
}

export interface TrusteeCompanySubscriptionProduct {
  id: string;
  name: string;
  fundCode?: string | null;
}

export interface TrusteeCompanySubscription {
  id: string;
  companyVan?: TrusteeCompanySubscriptionVan | null;
  product: TrusteeCompanySubscriptionProduct;
}

export interface Trustee extends SelectOptionsInterface {
  trusteeSchemes: TrusteeScheme[];
  company?: Company;
  trusteeCompanySubscriptions?: TrusteeCompanySubscription[];
  notifierRecords?: NotificationRecord[];
  trusteeContactPerson?: TrusteeContactPerson;
  postalAddress?: string;
  streetName?: string;
  location?: string;
}

export interface FailedDeduction {
  id: string;
  date: string;
  amount: number;
  reason: string;
}

export interface PreOpportunityInteraction {
  id: string;
  firstName?: string;
  lastName?: string;
  activityDate: string;
  email?: EmailInterface;
  phone: PhoneInterface;
  opportunityProspect: SelectOptionsInterface;
  interactionSource: SelectOptionsInterface;
  description: string;
}

export interface Port {
  id: string;
  name: string;
  key?: string;
  relationshipOfficer: User;
  relationshipManager: User;
  companies?: Company[];
}

export interface Team {
  id: string;
  name: string;
  key?: string;
  department: Department;
  users: User[];
}

export interface VestingRule {
  id: string;
  vestingType: string;
  flatPercentage?: number;
  year0: number;
  year1: number;
  year2: number;
  year3: number;
  year4: number;
  year5: number;
  year6: number;
  year7: number;
  year8: number;
  year9: number;
  year10: number;
  vestingDateType?: string;
}

export interface CustomerVestingRule {
  id: string;
  customerId: string;
  companySubscriptionId: string;
  vestingType: string;
  flatPercentage?: number;
  year0: number;
  year1: number;
  year2: number;
  year3: number;
  year4: number;
  year5: number;
  year6: number;
  year7: number;
  year8: number;
  year9: number;
  year10: number;
  vestingDateType?: string;
  customer?: Customer
}

export interface Employer {
  id: string;
  name: string;
  balance: number;
  redeemableBalance: number;
  redemptions: number;
  pending: number;
  fundCode: number;
}

export interface Scheme {
  id: string;
  name: string;
  tier: string;
  product: string;
  assetMapId: string;
  periodAnnualized: number;
  endOfPeriodReturn: number;
  periodIncome: number;
  beginningBalance: number;
  balance: number;
  liens: number;
  redeemableBalance: number;
  pending: number;
  redemptions: number;
  employers: Employer[];
  totalContributions: number;
  contributions?: Contribution[];
  beginningBalanceContributions?: Contribution[];
}

export interface Deal {
  id: string;
  clientPetraid: string;
  contributions?: any[];
  createdAt: string;
  customerPetraid: string;
  dealDescription: string;
  dealType: string;
  notificationProcessedAt?: string;
  priceDate?: string;
  relatedPaymentId?: string;
  relatedScheduleId?: string;
  shareClass: string;
  shareClassId: string;
  status: string;
  taId: string;
  totalAmount: number
  totalUnits: number;
  transactionId: string;
  transactionType: string;
  updatedAt: string;
}

export interface DealContributionMapping {
  deal_contribution_type: string;
  deal_contribution_column_name: string;
  value: number;
}

export interface CustomerProductBalance {
  amount: number;
  units: number;
  client: string;
  customer: string;
  shareClass: string;
  pendingUnits: number;
  redeemableAmount: number;
  redeemableUnits: number;
  redeemableContributionAmounts: DealContributionMapping[];
  redeemableContributionUnits: DealContributionMapping[];
  contributionAmounts: DealContributionMapping[];
  contributionUnits: DealContributionMapping[];
  price: { price: string; date: string };
  pendingRedemptionsAmount?: number;
  liens?: number;
  approvedRedemptionsAmount: number;
  vestedPercentage?: number;
  vestingMessage?: string;
}

export interface ExclusionList {
  id: string;
  name: string;
  description: string;
  exclusionListType: string;
  statementType: string;
  companies?: Company[];
  customers?: Customer[];
}

export interface CompanyExclusionList {
  exclusionList: ExclusionList;
  companies: Company[];
}

export interface RowSelection {
  selectedIdsToExport: React.Key[];
  type: "checkbox" | "radio";
  onChange: (selectedItems: React.Key[]) => void;
}

export enum PaginatedResourcePageSelected {
  currentEmployees,
  formerEmployees,
}

export interface searchQueryParams {
  page: number;
  q: string;
}

export interface EconomicIndicator {
  date: string;
  id: number;
  inflation: number;
  tbill: number;
}

export interface AssetAllocation {
  id: number;
  assetAllocationItem?: AssetAllocationItem;
  assetAllocationRate?: AssetAllocationRate;
  enable: boolean;
  order: string;
  product?: Product;
  quarter?: Quarter;
  rate: number;
}

export interface AssetAllocationItem {
  id: number;
  name: string;
}

export interface AssetAllocationRate {
  id: number;
  name: string;
}

export interface Quarter {
  assetAllocations: AssetAllocation;
  economicOutlooks: EconomicOutlook;
  endDate: string;
  id: number;
  name: string;
  displayName: string;
  startDate: string;
  cediDepreciation?: number;
  gdp?: number;
  inflation?: number;
  tbill91?: number;
  tbill182?: number;
  tbill365?: number;
}

export interface EconomicOutlook {
  id: number;
  outlook: string;
  createdAt: string;
  quarter: Quarter;
}

export interface CustomerStatement {
  client: string;
  dateSent: string;
  emailResentAt: string;
  emailSent: boolean;
  period: string;
  product: string;
  smsResentAt: string;
  smsSent: boolean;
  url: string;
}

export interface CompanyStatement {
  statementType: String;
  dateSent: string;
  emailResentAt: string;
  emailSent: boolean;
  period: string;
  product: string;
  url: string;
}

export interface ValidateAccount {
  status: boolean;
  message: string;
  name: string;
  score: number;
}

export interface PayoutVerificationFailure {
  bankAccountName: string;
  score: number;
}

export type Account = PayoutAccount | PaymentAccount;

export interface Director {
  id: string;
  firstName: string;
  lastName: string;
  emails: EmailInterface[];
  phones: PhoneInterface[];
  companies: Company[];
}

export interface FileUpload {
  createdAt?: string;
  fileType: string;
  id: string;
  state?: string;
  url: string;
  errorFileUrl?: string;
  source?: string;
  company?: Company;
  user?: User;
  name?: string;
}

export interface TransitionToNextState {
  event: string;
  state: string;
  amount_paid_required?: boolean;
  correct_value_date_required?: boolean;
  value_date_required?: boolean;
  reason_required?: boolean;
  paid_date_required?: boolean;
  first_contribution_date_required?: string;
  proof_of_contribution_document_url_required?: string;
  complete_disbursement?: boolean;
  create_payment?: boolean;
  issued_date_required?: boolean;
  create_payment_instruction?: boolean;
  vesting_value_required?: boolean;
  vesting_rule_url_required?: boolean;
}

export interface CompanySegments {
  strategic: string;
  growth: string;
  niche: string;
  maintenance: string;
  red: string;
}

export type ActiveDrawerContent =
  | "audits"
  | "notifierRecords"
  | "customerInteraction"
  | "transferRequests"
  | "redemptionRequests"
  | "liens"
  | "selfServiceActivity"
  | "vans"
  | "productSubscription"
  | "loanRequest"
  | "fixedIncomeRequest"
  | "syncLogs"
  | "mergers"
  | "";

export enum Loading {
  INITIAL = "initial",
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
}

export type SearchPayload<T> = {
  count: number;
  results: T[];
};

export interface Provider {
  id: string;
  name: string;
  contactEmail?: string;
  ccEmails?: string[];
  createdAt: string;
  updatedAt: string;
  products?: Product[];
  activeProducts?: Product[];
  activeProviderProducts?: ProviderProduct[];
  securedRequiredDocuments?: string[];
  semiSecuredRequiredDocuments?: string[];
  unsecuredRequiredDocuments?: string[];
  promo100RequiredDocuments?: string[];
  termsAndConditions?: string;
}

export interface ProviderProduct {
  id: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  provider: Provider;
  product: Product;
}

export interface LoanRequest {
  id: string;
  customer: Customer;
  company: Company;
  provider: Provider;
  product: Product;
  customerSubscription?: Subscription;
  loanRequestKyc?: LoanRequestKyc;
  loanType?: string;
  loanSecurityType?: string;
  state?: string;
  loanAmountRequested?: number;
  loanTenureMonths?: number;
  loanPurpose?: string;
  maxLoanAmount?: number;
  loanDuration?: number;
  portionOfContributionToBeUsed?: string[];
  canTransitionToNextStates?: any;
  createdAt: string;
  updatedAt: string;
  customerLien?: CustomerLien;
  finalLoanBreakdown?: LoanBreakdownData;
  loanBreakdown?: LoanBreakdownData;
  source?: string;
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  applicationFormUrl?: string;
  filledApplicationFormUrl?: string;
  filledApplicationFormStatus?: string;
  filledApplicationFormFailureReason?: string;
  filledApplicationFormFailedAt?: string;
  threeMonthsPayslipUrl?: string;
  standingOrderUrl?: string;
  threeMonthBankStatementUrl?: string;
  threeMomoStatementUrl?: string;
  oneYearConsistentTier2ContributionUrl?: string;
  clearanceFormUrl?: string;
  settlementQuotationUrl?: string;
  sixMonthsBankStatementUrl?: string;
  sixMonthsPayslipsUrl?: string;
  employmentOfferLetterUrl?: string;
  borrowersMandateUrl?: string;
  guarantorFormsUrl?: string;
  salesPerson?: SalesPerson;
  notifierRecords?: NotificationRecord[];
  disbursedAt?: string;
  disbursedAmount?: number;
  loanRequestNotes: loanRequestNotes[];
}

export interface loanRequestNotes {
  id: string;
  note: Note;
  loanRequest: LoanRequest;
  loanRequestNoteTickets?: NoteTicket[];
}

export interface LoanRequestKyc {
  id: string;
  loanRequest: LoanRequest;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  residentialAddress?: string;
  occupation?: string;
  employerName?: string;
  monthlyIncome?: number;
  idType?: Id;
  idNumber?: string;
  idExpiryDate?: string;
  maritalStatus?: MaritalStatus;
  numberOfDependents?: number;
  nextOfKinName?: string;
  nextOfKinRelationship?: string;
  nextOfKinPhone?: string;
  nextOfKinAddress?: string;
  collateralType?: string;
  collateralDescription?: string;
  collateralValue?: number;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  modeOfRepayment?: string;
  notes?: string;
  title?: string;
  maidenName?: string;
  contactPersonTitle?: string;
  nationality?: string;
  hometown?: string;
  secondaryPhoneNumber?: string;
  educationalQualification?: string;
  digitalAddress?: string;
  postalAddress?: string;
  townCity?: string;
  nearestLandmark?: string;
  residencyYears?: number;
  residenceType?: string;
  idIssueDate?: string;
  dateEmployed?: string;
  designation?: string;
  officeLocation?: string;
  employerEmail?: string;
  employerPhone?: string;
  employmentStatus?: string;
  employeeStaffId?: string;
  netMonthlyIncome?: number;
  totalAllowance?: number;
  totalDeductions?: number;
  salaryPaymentDay?: string;
  tin?: string;
  officeLandmark?: string;
  officeTownCity?: string;
  officeDigitalAddress?: string;
  fundUsedToSecure?: string;
  disbursementMode?: string;
  disbursementIsSalaryAccount?: boolean;
  secondaryRepaymentMode?: string;
  contactPersonEmail?: string;
  contactPersonOccupation?: string;
  contactPersonPlaceOfWork?: string;
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  applicationFormUrl?: string;
  threeMonthsPayslipUrl?: string;
  standingOrderUrl?: string;
  threeMonthBankStatementUrl?: string;
  threeMomoStatementUrl?: string;
  oneYearConsistentTier2ContributionUrl?: string;
  clearanceFormUrl?: string;
  settlementQuotationUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContributionAllocationItem {
  [key: string]: string | number;
}
export interface LoanAmountAllocationItem {
  [key: string]: string;
}

export interface AllocationsData {
  contribution_amount: string;
  contribution_allocations: ContributionAllocationItem[];
  redeemable_contribution_amount: string;
  redeemable_contribution_allocations: ContributionAllocationItem[];
  loan_amount_allocations: LoanAmountAllocationItem[];
}

export interface LoanBreakdownData {
  success: boolean;
  error?: string;
  data: {
    allocations: {
      contribution_amount: string;
      redeemable_contribution_amount: string;
      contribution_allocations: ContributionAllocationItem;
      redeemable_contribution_allocations: ContributionAllocationItem;
      loan_amount_allocations: ContributionAllocationItem;
    };
    eligible_for_loan: boolean;
    loanable_amount: string;
    last_scheme_contribution_amount: string;
    lien_breakdown: ContributionAllocationItem;
    lien_allocations: ContributionAllocationItem;
    price_date: string;
    total_lien_amount: string;
  };
}

export interface FixedIncomeOffer {
  id: string;
  product?: Product;
  rate?: number;
  minAmount?: number;
  maxAmount?: number;
  isActive?: boolean;
  tenure?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface FixedIncomeRequestReason {
  id: string;
  name: string;
  description?: string;
  key?: string;
  status?: string;
}

export interface FixedIncomeRequestStateReason {
  id: string;
  fixedIncomeRequestId: string;
  fixedIncomeRequestReasonId: string;
  fixedIncomeRequestReason: FixedIncomeRequestReason;
  comment?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FixedIncomePaymentInstruction {
  id: string;
  fixedIncomeRequest?: FixedIncomeRequest;
  assignedTo?: User;
  state?: string;
  amountsPaid?: number[];
  totalAmountPaid?: number;
  rate?: number;
  tenure?: number;
  isin?: string;
  receiptUrl?: string;
  transactionType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AllocationBreakdown {
  success: boolean;
  error: any;
  data: {
    expected_initial_return_on_maturity_data: {
      expected_total_amount: string;
      expected_interest: string;
    };
    matured_redemption_data: {
      total_matured_amount: string;
      total_matured_interest: string;
    };
    redemption_data: {
      total_redemption_amount: string;
      redemption_interest_earned: string;
      redemption_interest_penalty: string;
      redemption_amount: string;
      rediscount_date: string;
      loss: string;
      accrued_amount?: string;
      service_cost_rate?: number;
      service_cost?: string;
      csd_charge_rate?: number;
      csd_charge?: string;
      net_available_amount?: string;
      remaining_balance?: string;
      remaining_tenor?: number;
    };
    initial_investment_amount: string;
    redeemable_amount: string;
    maturity_date: string;
    matures_in: number;
    days_invested: number;
    interest_rate: string;
    tenure: number;
    timing: "early" | "matured";
    redemption_type: "full" | "partial";
    treasury_bill?: boolean;
  };
}

export interface FixedIncomeRedemption {
  id: string;
  amount: number;
  interestEarned?: number;
  penalty?: number;
  redemptionType: string;
  redemptionTiming: string;
  reference?: string;
  state: string;
  allocationBreakdown?: any;
  metaData?: any;
  fixedIncomeRequestId: string;
  createdAt: string;
  updatedAt: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  paymentMethod: string;
  canTransitionToNextStates?: TransitionToNextState[];
  rollOver?: boolean;
  rolledOverFixedIncomeRequest?: FixedIncomeRequest;
  assignedTo?: User;
  totalRedemptionAmount?: number;
  serviceCost?: number;
  csdCharge?: number;
  remainingBalance?: number;
  remainingTenor?: number;
  fixedIncomeRequest?: FixedIncomeRequest;
  supportingDocumentUrl?: string;
  notifierRecords?: NotificationRecord[];
}

export interface FixedIncomePayment {
  id: string;
  paymentReceiptUrl?: string;
  amount?: number;
  paymentDate?: string;
  accountName?: string;
  accountNumber?: string;
  metaData?: any;
  fixedIncomeRequest?: FixedIncomeRequest;
  createdAt: string;
  updatedAt: string;
  state?: string;
}

export interface FixedIncomeRequestNote {
  id: string;
  note: Note;
  fixedIncomeRequest: FixedIncomeRequest;
  fixedIncomeRequestNoteTickets?: NoteTicket[];
}

export interface FixedIncomeRequest {
  id: string;
  createdAt: string;
  updatedAt: string;
  state?: string;
  customer: Customer;
  provider: Provider;
  product: Product;
  assignedTo: User;
  source?: string;
  issuedDate?: string;
  maturityDate?: string;
  maturesIn?: number;
  daysInvested?: number;
  matured?: boolean;
  redeemableAmount?: number;
  fixedIncomeRequestKyc?: FixedIncomeRequestKyc;
  fixedIncomeRequestNotes?: FixedIncomeRequestNote[];
  fixedIncomePayments?: FixedIncomePayment[];
  fixedIncomeRedemptions?: FixedIncomeRedemption[];
  fixedIncomeRequestStateReasons?: FixedIncomeRequestStateReason[];
  fixedIncomePaymentInstruction?: FixedIncomePaymentInstruction;
  canTransitionToNextStates?: TransitionToNextState[];
  allocationBreakdown?: AllocationBreakdown;
  notifierRecords?: NotificationRecord[];
  sourceRedemption?: FixedIncomeRedemption;
  customerSubscription?: Subscription;
}

export interface FixedIncomeRequestKycBeneficiary {
  id?: string;
  beneficiaryType?: string;
  beneficiaryTitle?: string;
  beneficiaryFullName?: string;
  beneficiaryGender?: string;
  beneficiaryMaritalStatus?: string;
  beneficiaryRelationship?: string;
  beneficiaryDateOfBirth?: string;
  beneficiaryPlaceOfBirth?: string;
  beneficiaryResidentialStatus?: string;
  beneficiaryCountryOfOrigin?: string;
  beneficiaryCountryOfResidence?: string;
  beneficiaryResidentialAddress?: string;
  beneficiaryPercentageOfBenefit?: number;
  beneficiaryDigitalAddress?: string;
  beneficiaryOccupation?: string;
  beneficiaryTaxIdentificationNumber?: string;
  beneficiaryMobileNumber1?: string;
  beneficiaryMobileNumber2?: string;
  beneficiaryGhanaCardUrl?: string;
  beneficiaryPassportUrl?: string;
}

export interface RiskProfileDetails {
  [key: string]: any;
}

export interface RiskProfile {
  finalScore: number;
  riskLevel: string;
  factorsEvaluated: number;
  totalPossibleFactors: number;
  details: RiskProfileDetails;
  missingDetails: RiskProfileDetails;
  computedAt: string;
}

export interface FixedIncomeRequestKyc {
  id: string;
  customerId: string;
  customer?: Customer;
  fixedIncomeRequestId?: string;
  title?: string;
  surname?: string;
  firstName?: string;
  otherNames?: string;
  fullName?: string;
  gender?: string;
  maritalStatus?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  mothersMaidenName?: string;
  residentialStatus?: string;
  countryOfOrigin?: string;
  countryOfResidence?: string;
  residentialAddress?: string;
  digitalAddress?: string;
  mobileNumber?: string;
  emailAddress?: string;
  residentPermitNumber?: string;
  placeOfIssue?: string;
  permitIssueDate?: string;
  permitExpiryDate?: string;
  nameOfEmployerSchool?: string;
  addressOfEmployerSchool?: string;
  cityTown?: string;
  natureOfBusiness?: string;
  businessSchoolOfficeContactNumber?: string;
  businessSchoolOfficeEmail?: string;
  yearsOfEmployment?: number;
  yearsOfCurrentEmployment?: number;
  yearsOfPreviousEmployment?: number;
  occupation?: string;
  taxIdentificationNumber?: string;
  profession?: string;
  professionalLicenseNumber?: string;
  communicationMethod?: string[];
  kycDeclaration?: string;
  kycDocuments?: string[];
  isCitizenOfForeignCountry?: boolean;
  holdsPassportOfForeignCountry?: boolean;
  holdsGreenCardOfForeignCountry?: boolean;
  isResidentInForeignCountry?: boolean;
  spentMoreThan183DaysInForeignCountry?: boolean;
  foreignFullName?: string;
  foreignResidentialAddress?: string;
  foreignMailingAddress?: string;
  foreignTelephoneNumber?: string;
  foreignTinSsnNationalId?: string;
  approximateAnnualIncome?: string;
  investmentCurrency?: string;
  investmentHorizon?: string;
  objectives?: string;
  investmentKnowledge?: string;
  riskTolerance?: string;
  modeOfStatementDelivery?: string[];
  statementFrequency?: string;
  statementFrequencyOther?: string;
  hasBoughtSecuritiesBefore?: boolean;
  existingCsdClientId?: string;
  clientType?: string;
  verifiedByCsdOfficer?: string;
  csdOfficerSignatureUrl?: string;
  csdVerificationDate?: string;
  csdStampUrl?: string;
  clientCsdSecuritiesAccountNumbers?: string;
  createdAt?: string;
  updatedAt?: string;
  employmentStatus?: string;
  payoutCurrency?: string;
  payoutAccountName?: string;
  payoutAccountNumber?: string;
  payoutBankName?: string;
  payoutBankBranchName?: string;
  payoutPaymentMethod?: string;
  beneficiaryType?: string;
  beneficiaries: FixedIncomeRequestKycBeneficiary[];
  residentRegion?: string;
  residentPermitStatus?: string;
  residentCity?: string;
  sourceOfFunds?: string;
  expectedDepositAmount?: string;
  expectedWithdrawalAmount?: string;
  expectedFrequencyOfDeposit?: string;
  expectedFrequencyOfWithdrawal?: string;
  accountOrigination?: string;
  pepStatus?: string;
  riskProfile?: RiskProfile;
  passportUrl?: string;
}

export interface CustomerBalanceV2 {
  allocations?: any;
  applicableAllocations?: string[];
  lienDeductions?: any;
  pendingRedemptionDeductions?: any;
  price?: number;
  priceDate?: string;
  productName?: string;
  productTier?: string;
  redeemableAllocations?: any;
  schemeBalance?: number;
  schemeBeginningBalance?: number;
  schemePeriodIncome?: number;
  schemeRedeemableBalance?: number;
  schemeRedemptions?: number;
  schemeTotalContributions?: number;
  totalAllocations?: any;
  totalLienDeductions?: number;
  totalPendingRedemptionDeductions?: any;
  totalRedeemableAllocations?: number;
  vestingComputations?: any;
  pendingRedemptionRequests?: any[];
  pendingCustomerTransactions?: any[];
  vestedAllocations?: any;
  totalVestedAllocations?: any;
  unvestedAllocations?: any;
  totalUnvestedAllocations?: any;
}

export interface KycEnumOptions {
  [key: string]: string[];
}

export interface GridColConfig {
  span: number;
  offset?: number;
}

export interface MergerSectionProps {
  mergeLogs: CustomerMergeLog[] | CompanyMergeLog[];
  mergedTo?: any; // Customer or Company
  onReverse: (duplicateId: string) => void;
  onReverseAll?: (duplicateIds?: string[]) => void;
  onInitiate: () => void;
  isReversing: boolean;
  isBulkReversing?: boolean;
  type: "customer" | "company";
  canManage: boolean;
}
